import { ApiClient } from "./apiClient";
import { ApiResponse, PaginatedResponse } from "../types/common";
import { MediaFile, MediaFilterRequest, UpdateMediaRequest } from "../types/media";

// Admin API client for media library
const mediaClient = new ApiClient<MediaFile>("/admin/media");

// =====================
// MEDIA LIBRARY ENDPOINTS
// =====================

/**
 * Get all media files with filtering and pagination
 */
export const getAllMedia = async (
  filter?: MediaFilterRequest
): Promise<ApiResponse<PaginatedResponse<MediaFile>>> => {
  const params = buildFilterParams(filter);
  const queryString = params.toString();
  return mediaClient.custom("get", `${queryString ? `?${queryString}` : ""}`);
};

/**
 * Get a single media file by ID
 */
export const getMediaById = async (
  id: string
): Promise<ApiResponse<MediaFile>> => {
  return mediaClient.custom("get", `/${id}`);
};

/**
 * Get all unique tags used across media files
 */
export const getAllTags = async (): Promise<ApiResponse<string[]>> => {
  return mediaClient.custom("get", "/tags");
};

/**
 * Update media file metadata (alt text, tags)
 */
export const updateMedia = async (
  id: string,
  data: UpdateMediaRequest
): Promise<ApiResponse<MediaFile>> => {
  return mediaClient.custom("put", `/${id}`, data);
};

/**
 * Delete a media file record (optionally delete physical file)
 */
export const deleteMedia = async (
  id: string,
  deletePhysicalFile = false
): Promise<ApiResponse<boolean>> => {
  return mediaClient.custom("delete", `/${id}?deleteFile=${deletePhysicalFile}`);
};

/**
 * Scan existing uploads folder and register files in the media library
 */
export const scanExistingFiles = async (
  mediaType = "Product"
): Promise<ApiResponse<number>> => {
  return mediaClient.custom("post", `/scan?mediaType=${mediaType}`);
};

// =====================
// HELPERS
// =====================

function buildFilterParams(filter?: MediaFilterRequest): URLSearchParams {
  const params = new URLSearchParams();

  if (filter?.page) params.append("page", filter.page.toString());
  if (filter?.pageSize) params.append("pageSize", filter.pageSize.toString());
  if (filter?.search) params.append("search", filter.search);
  if (filter?.mediaType) params.append("mediaType", filter.mediaType);
  if (filter?.tags && filter.tags.length > 0) {
    filter.tags.forEach((tag) => params.append("tags", tag));
  }
  if (filter?.uploadedAfter) params.append("uploadedAfter", filter.uploadedAfter);
  if (filter?.uploadedBefore) params.append("uploadedBefore", filter.uploadedBefore);
  if (filter?.sortBy) params.append("sortBy", filter.sortBy);
  if (filter?.sortDescending !== undefined) {
    params.append("sortDescending", filter.sortDescending.toString());
  }

  return params;
}

export { mediaClient };
